import { apiGet, apiPost, apiPut } from '@/utils/api';
import getPath from '@/utils/getPath';
import { handleApiError } from '@/utils/errorHandler';

export const workItemApi = {
  /**
   * Fetches a work item by id.
   * @apiRoute GET '/workItems/<work_item_id>'
   */
  async getWorkItem(id) {
    try {
      const response = await apiGet(getPath(`workItems/${id}`));
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getWorkItem', false);
    }
  },

  /**
   * Sets assignment status for a work item.
   * @apiRoute PUT '/assignments/<work_item_id>'
   */
  async setWorkItemStatus(workItemId, status) {
    try {
      const response = await apiPut(
        getPath(`assignments/${workItemId}?action=${status}`)
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'setWorkItemStatus', false);
    }
  },

  /**
   * Fetches activity subtype lookup entries.
   * @apiRoute GET '/enumeration/activity-subtype'
   */
  async getActivitySubtypes() {
    try {
      const response = await apiGet(getPath('enumeration/activity-subtype'));
      return response;
    } catch (err) {
      await handleApiError(err, 'getActivitySubtypes', false);
    }
  },

  /**
   * Fetches work item activities.
   * @apiRoute GET '/workItems/<work_item_id>/activities'
   */
  async getWorkItemActivities({
    id,
    direction = 'desc',
    field = 'mod_timest',
  }) {
    try {
      const params = new URLSearchParams({
        field,
        direction,
        type: 'activities',
      });

      const response = await apiGet(
        getPath(`workItems/${id}/activities`),
        params
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getWorkItemActivities', false);
    }
  },

  /**
   * Fetches work item charges.
   * @apiRoute GET '/workItems/<work_item_id>/activities'
   */
  async getWorkItemCharges({
    id,
    direction = 'asc',
    field = 'mod_timest',
  }) {
    try {
      const params = new URLSearchParams({
        field,
        direction,
        type: 'charges',
      });

      const response = await apiGet(
        getPath(`workItems/${id}/activities`),
        params
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getWorkItemCharges', false);
    }
  },

  /**
   * Fetches work item transactions.
   * @apiRoute GET '/workItems/<work_item_id>/activities'
   */
  async getWorkItemTransactions({
    id,
    direction = 'asc',
    field = 'mod_timest',
  }) {
    try {
      const params = new URLSearchParams({
        field,
        direction,
        type: 'tx',
      });

      const response = await apiGet(
        getPath(`workItems/${id}/activities`),
        params
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getWorkItemTransactions', false);
    }
  },

  /**
   * Triggers claim status lookup.
   * @apiRoute POST '/workItems/<work_item_id>/claimStatus'
   */
  async getClaimStatus({ workItemId, tin, planId, lookupValue, replyTo }) {
    try {
      const payload = {
        tin,
        plan_id: planId,
        lookup_value: lookupValue,
        reply_to: replyTo,
      };

      const response = await apiPost(
        getPath(`workItems/${workItemId}/claimStatus`),
        payload
      );

      return response.data;
    } catch (err) {
      await handleApiError(
        err,
        'getClaimStatus',
        false,
        'Unable to retreive claim status. Please check directly with the payer.'
      );
    }
  },

  /**
   * Posts a work item activity.
   * @apiRoute POST '/workItems/<work_item_id>/activities'
   */
  async postActivity({
    workItemId,
    activityId,
    activityName,
    subactivityId,
    subActivityName,
    notes,
    ua_id,
    auxo_user_id,
    draft_id = null,
  }) {
    try {
      const payload = {
        activity_type_id: activityId,
        activity_type: activityName,
        activity_subtype_id: parseInt(subactivityId, 10),
        activity_subtype: subActivityName,
        note: notes,
        ua_id,
        auxo_user_id,
        draft_id,
      };

      const response = await apiPost(
        getPath(`workItems/${workItemId}/activities`),
        payload
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'postActivity', false);
    }
  },

  /**
   * Edits or deletes a work item activity.
   * @apiRoute PUT '/workItems/<work_item_id>/activities/<activity_id>'
   */
  async editWorkItemActivity({
    workItemId,
    activityId,
    actorEmail,
    note,
    updateType,
    draft_id = null,
  }) {
    try {
      const payload = {
        actor_email: actorEmail,
        note,
        update_type: updateType,
        draft_id,
      };

      const response = await apiPut(
        getPath(`workItems/${workItemId}/activities/${activityId}`),
        payload
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'editWorkItemActivity', false);
    }
  },

  /**
   * Fetches activity draft for a user/work item.
   * @apiRoute GET '/workItems/<work_item_id>/activities/drafts/<auxo_user_id>'
   */
  async getNoteDraft(workItemId, userId) {
    try {
      const response = await apiGet(
        getPath(`workItems/${workItemId}/activities/drafts/${userId}`)
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getNoteDraft', false);
    }
  },

  /**
   * Saves activity note draft.
   * @apiRoute PUT '/workItems/<work_item_id>/activities/drafts'
   */
  async saveNoteDraft({
    workItemId,
    auxo_user_id,
    note,
    draftId,
    activityTypeId = null,
    activitySubtypeId = null,
  }) {
    try {
      const payload = {
        auxo_user_id,
        note,
        work_item_id: workItemId,
        draft_id: draftId,
        activity_type_id: activityTypeId,
        activity_subtype_id: activitySubtypeId,
      };

      const response = await apiPut(
        getPath(`workItems/${workItemId}/activities/drafts`),
        payload
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'saveNoteDraft', false);
    }
  },
};
